import {
  FC, useEffect, useMemo, useState, 
} from 'react';
import { useTranslation } from 'react-i18next';

import useMaskedInput from 'hooks/useMaskedInput';
import useCroptimizerSoil from 'api/hooks/useCroptimizerSoil';
import {
  CropProps,
  CropTableProps,
  CropTypes,
  SoilTypes,
  TypeOfKey,
} from 'utils/types';

import plusIcon from '/plusBlue.svg';
import resetIcon from '/resetIcon.svg';
import deleteIcon from '/cropDelete.svg';
import duplicateIcon from '/cropDuplicate.svg';

import { FieldProps } from 'features/Croptimizer/types';
import {
  HaToacOnly,
  kgToLb,
  numericValues,
  unMaskInput,
  unMaskInputValue,
} from 'utils/helpers';
import Button from 'components/Button';
import Modal from 'components/Modal';
import { errorToast } from 'utils/toast';
import { Metric } from 'utils/constants';
import { calcCurrentPriceByCropNSoil } from 'calculations/german/calcCurrentPriceByCropNSoil';
import { useNextHandler } from 'features/Croptimizer/providers/NextHandlerContext';
import { scrollTop } from 'utils/scroll-top';
import { cn } from 'utils/cn';
import { tableTargetStatus } from 'calculations/german/calculationValues';
import { useCroptimizer } from 'features/Croptimizer/providers/CroptimizerContext';

import { GermanStatusCropRotation } from './components/GermanStatusCropRotation';

import CropFieldTotal from '../SharedComponents/CropFieldTotal';
import ProductCrop from '../SharedComponents/ProductCrop';
import EditCropModal from '../SharedComponents/Modals/EditCropModal';
import AddCropModal from '../SharedComponents/Modals/AddCropModal';
import CropTable from '../SharedComponents/Table';
import CropOm from '../SharedComponents/CropOm';
import CropFields from '../SharedComponents/CropFields';
import { getP39Value } from '../../../../utils/calcHelperBoard';
import { useDuplicateField } from '../../hooks/useDuplicateField';
import { useResetData } from '../../hooks/useResetData';
import { useGenerateCrop } from '../../hooks/useGenerateCrop';

interface Props {
  id: number;

  dublicateField?: any;
  object: FieldProps['object'];
  onDelete: () => void;
  cropList?: CropProps[];
  currentCrop: CropProps;
  setCropTableToFieldObject: (
    tableDataOrUpdater:
    | CropTableProps[]
    | ((prev: CropTableProps[]) => CropTableProps[])
  ) => void;
  setFieldObjectField: (
    name: keyof Required<FieldProps>['object']
  ) => (value: TypeOfKey<Required<FieldProps>['object']>) => void;
  isPDF?: boolean;
}

export const GermanFieldComponent: FC<Props> = ({
  id,
  object,
  onDelete,
  cropList,
  currentCrop,
  setCropTableToFieldObject: setTableData,
  setFieldObjectField,
  isPDF,
}) => {
  const {
    fields,
    activeTabCrop,
    disabledState,
    mineralizationValues,
    fertilizationValues,
    setDisabledState,
    setActiveTab,
    boardSoilValues,
    metricValue,
    products,
    setFields,
    crops,
    setCrops,
  } = useCroptimizer();
  const { resetData } = useResetData(currentCrop);
  const { generateCrop } = useGenerateCrop();
  const phAvg = mineralizationValues?.annualDecrease ?? '0.1';
  const fineAndDry = unMaskInput(boardSoilValues?.fineDrySoil ?? '20'); 

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { setHandler, setShowBack, setShowNext, setBackHandler, setDisabled } =
    useNextHandler();
  const [errorCurrent, setErrorCurrent] = useState('');
  const [errorTarget, setErrorTarget] = useState('');
  const [errorTargetSec, setErrorTargetSec] = useState('');

  const { value: name, setValue: setName } = useMaskedInput({
    value: fields.find((el) => el.id === id)?.title ?? '',
    defaultValue: '',
    onChangeCallback: (value) => {
      setFields((prev: FieldProps[]) => prev.map((item) => (item.id === id ? { ...item,
        title: value } : item)),
      );
    },
  });

  useEffect(() => {
    if (name.length > 0) {
      const filtered = fields.map((field) => {
        if (field.id === activeTabCrop) {
          return {
            ...field,
            id: field.id,
            title: name,
          };
        }

        return field;
      });
      setFields(filtered);
    }

    if (!name.length) {
      const filtered = fields.map((field) => {
        if (field.id === activeTabCrop) {
          return {
            ...field,
            id: field.id,
            title: '',
          };
        }

        return field;
      });

      setFields(filtered);
    }
  }, [name]);

  const getDisabled = useMemo(
    () => disabledState.includes(name),
    [crops.length],
  );

  const getNameDisabled = useMemo(() => {
    let result = false;

    if (crops.length > 0) {
      crops.forEach((crop) => {
        if (crop.fieldName === name) {
          result = true;
        }
      });
    }

    return result;
  }, [crops, name]);

  // --------------------------------------------------------------------------------------- Status States
  const { t } = useTranslation();
  const {
    value: currentph,
    setValue: setCurrentPh,
    clean: cleancurrentph,
  } = useMaskedInput({
    defaultValue: object?.currentphI ?? '5,8',
    value: object?.currentphI ?? '5,8',
    onChangeCallback: setFieldObjectField('currentphI'),
  });
  const {
    value: currentSec,
    setValue: setCurrentSec,
    clean: cleancurrentsec,
  } = useMaskedInput({
    defaultValue: object?.currentSecI ?? '',
    value: object?.currentSecI,
    onChangeCallback: (value) => setFieldObjectField('currentSecI')(value),
  });
  const {
    value: targetph,
    setValue: setTargetPh,
    clean: cleantargetph,
  } = useMaskedInput({
    defaultValue: object?.targetphI ?? '6,3',
    value: object?.targetphI ?? '6,3',
    onChangeCallback: (value) => {
      setFieldObjectField('targetphI')(value);
    },
  });

  const [initialTargetPh, setInitialTargetPh] = useState(targetph);
  const isTargetPhChanged = initialTargetPh !== targetph;

  // --------------------------------------------------------------------------------------- CropFields States

  const { data: soils } = useCroptimizerSoil();

  const {
    value: area,
    setValue: setArea,
    clean: cleanarea,
  } = useMaskedInput({
    defaultValue:
      object?.areaI ??
      unMaskInputValue(
        metricValue === Metric.metric ? '10' : String(HaToacOnly(10)),
      ),
    value:
      object?.areaI ??
      unMaskInputValue(
        metricValue === Metric.metric ? '10' : String(HaToacOnly(10)),
      ),
    onChangeCallback: setFieldObjectField('areaI'),
  });

  const { value: soilType, setValue: setSoilType } = useMaskedInput({
    defaultValue: object?.soilTypeI ?? '',
    value: object?.soilTypeI ?? '',
    onChangeCallback: (value) => {
      setFieldObjectField('soilTypeI')(value);

      if (soilType !== value && soilType?.length > 0 && soils) {
        const getSoil = soils?.find((soil) => soil.Soils === value);
        const result =
          metricValue === Metric.metric
            ? String(getSoil?.CEC)
            : String(kgToLb(Number(getSoil?.CEC)));

        setSec(unMaskInputValue(result));
        setInitialSec(unMaskInputValue(result));
      }
    },
    isDisableClean: true,
  });

  const {
    value: sec,
    setValue: setSec,
    clean: cleansec,
  } = useMaskedInput({
    defaultValue: object?.secI ?? '',
    value: object?.secI ?? '',
    onChangeCallback: setFieldObjectField('secI'),
  });
  const [initialSec, setInitialSec] = useState<string>(object?.secI ?? '');

  const soilSelect = useMemo(() => {
    if (soils) {
      return soils?.map((soil) => ({
        value: soil.Soils,
        label: soil.Soils,
      }));
    }

    return [];
  }, [soils]);

  useEffect(() => {
    if (soilSelect && !object?.soilTypeI) {
      setSoilType(soilSelect?.[0]?.value);
    }

    return;
  }, [soilSelect]);

  useEffect(() => {
    if (soilType?.length > 0 && soils) {
      const getSoil = soils?.find((soil) => soil.Soils === soilType);
      const result =
        metricValue === Metric.metric
          ? String(getSoil?.CEC)
          : String(kgToLb(Number(getSoil?.CEC)));
      setInitialSec(unMaskInputValue(result));
    }
  }, [soilType, soils, metricValue]);

  const getErrorOfArea = useMemo(() => {
    if (Number(cleanarea) < 1) {
      return `${t('minimumPossibleValue')} is 1`;
    }

    if (Number(cleanarea) > 10000) {
      return `${t('maximumPossibleValue')} is 10'000`;
    }

    return null;
  }, [area]);

  const errorSec = useMemo(() => {
    if (Number(cleansec) < 2) {
      return `${t('minimumPossibleValue')} 2`;
    }

    if (Number(cleansec) > 200) {
      return `${t('maximumPossibleValue')} 200`;
    }

    return null;
  }, [sec]);

  // --------------------------------------------------------------------------------------- OM States
  const {
    value: om,
    setValue: setOm,
    clean: cleanom,
  } = useMaskedInput({
    defaultValue: object?.omI ?? mineralizationValues?.organicMatter,
    value: object?.omI ?? mineralizationValues?.organicMatter,
    onChangeCallback: setFieldObjectField('omI'),
  });
  const {
    value: n,
    setValue: setN,
    clean: cleanN,
  } = useMaskedInput({
    defaultValue: object?.nI ?? fertilizationValues?.nprice,
    value: object?.nI ?? fertilizationValues?.nprice,
    onChangeCallback: setFieldObjectField('nI'),
  });
  const {
    value: p,
    setValue: setP,
    clean: cleanP,
  } = useMaskedInput({
    defaultValue: object?.pI ?? fertilizationValues?.pprice,
    value: object?.pI ?? fertilizationValues?.pprice,
    onChangeCallback: setFieldObjectField('pI'),
  });
  const {
    value: k,
    setValue: setK,
    clean: cleanK,
  } = useMaskedInput({
    defaultValue: object?.kI ?? fertilizationValues?.kprice,
    value: object?.kI ?? fertilizationValues?.kprice,
    onChangeCallback: setFieldObjectField('kI'),
  });
  const {
    value: s,
    setValue: setS,
    clean: cleanS,
  } = useMaskedInput({
    defaultValue: object?.sI ?? mineralizationValues?.sPrice,
    value: object?.sI ?? mineralizationValues?.sPrice,
    onChangeCallback: setFieldObjectField('sI'),
  });

  // --------------------------------------------------------------------------------------- Table States

  const tableData = object?.tableDataI || [];

  const [addCropModal, setAddCropModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalWithoutSupplyPlan, setDeleteModalWithoutSupplyPlan] =
    useState(false);
  const [editCropModal, setEditCropModal] = useState<CropTableProps | null>(
    null,
  );

  const getStatus = useMemo(() => {
    if (Number(cleancurrentph) < Number(cleantargetph)) {
      return `pH ${t('improving')}`;
    }

    return (
      'pH ' +
      t('maintained') +
      ` ${tableData?.length}` +
      `${tableData?.length === 1 ? ' year' : ' years'}`
    );
  }, [currentph, targetph, tableData, t]);

  useEffect(() => {
    const res: CropTableProps[] = [];

    if (cropList) {
      tableData?.forEach((item, i) => {
        const crop =
          currentCrop && currentCrop.Crops === item.name
            ? currentCrop
            : cropList.find((crop) => crop.Crops === item.name);

        if (crop) {
          const updatedCrop = generateCrop(crop, object, item.id, item, i);

          res.push(updatedCrop);
        }
      });

      // Only update if there are actual changes to prevent infinite loop
      const hasChanges = res.some((newItem, index) => {
        const oldItem = tableData[index];

        return (
          newItem.npkGains !== oldItem?.npkGains ||
          newItem.monps !== oldItem?.monps ||
          newItem.total !== oldItem?.total
        );
      });

      if (hasChanges) {
        setTableData(res);
      }
    }
  }, [
    currentph,
    targetph,
    cleancurrentph,
    cleantargetph,
    currentCrop,
    cleanom,
    cleanN,
    cleanP,
    cleanK,
    cleanS,
    cropList,
    object,
  ]);

  const errorOrganicMatter = useMemo(() => {
    if (Number(cleanom) < 0.5) {
      return `${t('minimumPossibleValue')} 0.5`;
    }

    if (Number(cleanom) > 50) {
      return `${t('maximumPossibleValue')} 50`;
    }

    return null;
  }, [om]);

  // --------------------------------------------------------------- Product States

  const { value: productType, setValue: setProductType } = useMaskedInput({
    defaultValue: object?.productTypeI ?? '',
    value: object?.productTypeI ?? '',
    onChangeCallback: (value) => {
      setFieldObjectField('productTypeI')(value);

      if (productType !== value && productType?.length > 0 && products) {
        const getProduct = products?.find(
          (product) => product.Products === value,
        );
        setNv(String(getProduct?.NV));
      }
    },
    isDisableClean: true,
  });

  const {
    value: nv,
    setValue: setNv,
    clean: cleannv,
  } = useMaskedInput({
    defaultValue: object?.nvI ?? '',
    value: object?.nvI ?? '',
    onChangeCallback: setFieldObjectField('nvI'),
  });
  const {
    value: mt,
    setValue: setMt,
    clean: cleanmt,
  } = useMaskedInput({
    defaultValue: object?.mtI ?? '',
    value: object?.mtI ?? '',
    onChangeCallback: setFieldObjectField('mtI'),
  });

  const productSelect = useMemo(() => {
    if (products) {
      return products?.map((product) => ({
        value: product.Products,
        label: product.Products,
      }));
    }

    return [];
  }, [products]);

  useEffect(() => {
    if (productSelect && !object?.productTypeI) {
      setProductType(productSelect?.[0]?.value);
    }

    return;
  }, [productSelect]);

  const getProductStatus = useMemo(() => {
    if (Number(cleancurrentph) < Number(cleantargetph)) {
      return t('improving');
    } else if (tableData?.length > 1) {
      return 'For ' + tableData?.length + ` ${t('years')}`;
    } else {
      return 'For ' + tableData?.length + ` ${t('year')}`;
    }
  }, [currentph, targetph, tableData, t]);

  const errorNv = useMemo(() => {
    if (Number(cleannv) < 10) {
      return `${t('minimumPossibleValue')} 10`;
    }

    if (Number(cleannv) > 100) {
      return `${t('maximumPossibleValue')} 100`;
    }

    return null;
  }, [nv]);

  const errorMt = useMemo(() => {
    if (Number(cleanmt) < 0) {
      return `${t('minimumPossibleValue')} 0`;
    }

    if (Number(cleanmt) > 999999) {
      return `${t('maximumPossibleValue')} 999'999`;
    }

    return null;
  }, [mt]);

  const getMtha = useMemo(() => {
    const NV = Number(cleannv);

    if (Number(cleancurrentph) < Number(cleantargetph)) {
      const newNV = NV / 100;

      const getTotalMtha = calcCurrentPriceByCropNSoil({
        soilType: soilType as SoilTypes,
        cropType: currentCrop.Crops as CropTypes,
        currentPh: cleancurrentph,
        targetPh: cleantargetph,
      });

      return String(
        (Math.round(Number(getTotalMtha) * 100) / 100 / newNV).toFixed(2),
      );
    } else {
      const secSoil = Number(cleansec);
      const p39 = getP39Value(secSoil, NV, 1);

      return (p39 * tableData.length) / 1000;
    }
  }, [
    cleansec,
    cleancurrentph,
    cleantargetph,
    cleannv,
    soilType,
    currentCrop.Crops,
  ]);

  const getEuroHa = useMemo(
    () => Math.round(Number(cleanmt) * Number(getMtha)),
    [getMtha, mt],
  );

  const getProductMt = useMemo(
    () => (Number(cleanarea) * Number(getMtha)).toFixed(1),
    [getMtha, area],
  );

  const getCost = useMemo(
    () => Math.round(Number(cleanmt) * Number(getProductMt)),
    [getProductMt, mt],
  );

  const totalTotal = useMemo(() => {
    let result = 0;
    tableData?.forEach((data) => {
      result += data.total;
    });

    return result;
  }, [tableData]);

  const getGains = useMemo(
    () => Number(totalTotal) * Number(cleanarea),
    [totalTotal, area],
  );

  const getROI = useMemo(
    () => Number(getGains) - Number(getCost),
    [getGains, getCost],
  );

  // --------------------------------------------------------------- Button Functionality

  const { duplicateField } = useDuplicateField();

  const onDuplicateField = () => {
    duplicateField({
      currentph: currentph,
      targetph: targetph,
      currentSec: numericValues(cleancurrentsec),
      area: area,
      phAvg: phAvg,
      soilType: soilType,
      sec: unMaskInputValue(cleansec),
      fineDry: fineAndDry ?? '20',
      om: numericValues(cleanom),
      n: cleanN,
      p: cleanP,
      k: cleanK,
      s: cleanS,
      tableData: tableData,
      productType: productType,
      nv: nv,
      mt: mt,
    });
  };

  const addToSupplyPlan = () => {
    if (crops?.length > 18) {
      errorToast('You can add only 20 fields to Supply Plan');
    } else {
      const data = {
        fieldName: name,
        currentph: cleancurrentph,
        targetph: cleantargetph,
        area: cleanarea,
        phAvg,
        soilType,
        fineAndDry: fineAndDry ?? '20',
        om: numericValues(cleanom),
        n: cleanN,
        p: cleanP,
        k: cleanK,
        s: cleanS,
        tableData,
        productType,
        mthaDose: getMtha,
        productmt: getProductMt,
        cost: getCost,
        gains: getGains,
        roi: getROI,
      };

      setCrops((prev: any) => [...prev, data]);

      setDisabledState((prev: string[]) => [...prev, name]);
    }
  };

  const [initialName, setInitialName] = useState<string>('');

  const updateInSupplyPlan = () => {
    const data = {
      fieldName: name,
      currentph: cleancurrentph,
      targetph: cleantargetph,
      area: cleanarea,
      phAvg,
      soilType,
      fineAndDry: fineAndDry ?? '20',
      om: numericValues(cleanom),
      n: cleanN,
      p: cleanP,
      k: cleanK,
      s: cleanS,
      tableData,
      productType,
      mthaDose: getMtha,
      productmt: getProductMt,
      cost: getCost,
      gains: getGains,
      roi: getROI,
    };

    const getField = crops?.filter((crop) => crop.fieldName !== initialName);
    const getall = [...getField, data];
    setCrops(getall);
    setIsEditing(false);
  };

  const onDeleteClick = () => {
    if (getDisabled) {
      setDeleteModal(true);
    } else {
      setDeleteModalWithoutSupplyPlan(true);
    }
  };

  useEffect(() => {
    if (!isTargetPhChanged && !!tableData.length) {
      const matchElIndex = tableData.findIndex(
        (el) => el.name !== CropTypes.Grassland,
      );
      const newTargetPh =
        matchElIndex !== -1
          ? tableTargetStatus[CropTypes.OtherCrops][soilType as SoilTypes]
          : tableTargetStatus[CropTypes.Grassland][soilType as SoilTypes];

      setInitialTargetPh(newTargetPh);
    }
  }, [isTargetPhChanged, tableData, soilType]);

  useEffect(() => {
    // Configure navigation handlers for the current step and enable Back/Next buttons

    setShowBack(true);
    setShowNext(true);
    setDisabled(
      !!getErrorOfArea ||
        !!errorOrganicMatter ||
        !!errorCurrent ||
        !!errorTarget ||
        !!errorTargetSec,
    );

    setBackHandler(() => {
      setActiveTab(2);
      scrollTop();
    });

    setHandler(() => {
      setActiveTab(4);
      scrollTop();
    });
  }, [
    getErrorOfArea,
    errorOrganicMatter,
    errorCurrent,
    errorTarget,
    errorTargetSec,
  ]);

  return (
    <>
      {id === activeTabCrop && (
        <>
          {getDisabled && (
            <div className="text-[#F8781B] font-medium mt-8">
              {t('fieldAddedToSupply')}
            </div>
          )}

          <GermanStatusCropRotation
            setCurrentSec={setCurrentSec}
            setTargetPh={setTargetPh}
            cleancurrentph={cleancurrentph}
            cleancurrentsec={cleancurrentsec}
            currentph={currentph}
            currentSec={currentSec}
            targetph={targetph}
            isTargetPhChanged={isTargetPhChanged}
            cleantargetph={cleantargetph}
            object={object}
            setFieldObjectField={setFieldObjectField}
            setCurrentPh={setCurrentPh}
            disabled={getDisabled && !isEditing}
            setErrorCurrent={setErrorCurrent}
            setErrorTarget={setErrorTarget}
            setErrorTargetSec={setErrorTargetSec}
            errorCurrent={errorCurrent}
            errorTarget={errorTarget}
            errorTargetSec={errorTargetSec}
          />

          <div className="border border-b my-8" />
          <CropFields
            name={name}
            setName={setName}
            area={area}
            setArea={setArea}
            getErrorOfArea={getErrorOfArea}
            phAvg={phAvg}
            soilSelect={soilSelect}
            soilType={soilType}
            setSoilType={setSoilType}
            sec={sec}
            initialSec={initialSec}
            setSec={setSec}
            errorSec={errorSec}
            fineDry={fineAndDry ?? '20'}
            getStatus={getStatus}
            disabled={getDisabled && !isEditing}
            nameError={getNameDisabled && !isEditing}
          />

          <div className="border border-b my-8" />
          <CropOm
            om={om}
            setOm={setOm}
            n={n}
            setN={setN}
            p={p}
            setP={setP}
            k={k}
            setK={setK}
            s={s}
            setS={setS}
            disabled={getDisabled && !isEditing}
            errorOrganicMatter={errorOrganicMatter}
          />

          <div className="border border-b my-8" />
          <div className={cn(isPDF && 'pt-1')}>
            <CropTable
              tableData={tableData}
              setTableData={setTableData}
              setEditCropModal={setEditCropModal}
              disabled={getDisabled && !isEditing}
              isPDF={isPDF}
              object={object}
            />
          </div>

          {!isPDF && tableData?.length < 10 && (!getDisabled || isEditing) && (
            <button
              onClick={() => setAddCropModal(true)}
              className={`flex items-center mt-4 w-[100px] ${
                getDisabled && !isEditing
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
            >
              <img className="w-[14px] h-[14px] mr-2" src={plusIcon} alt="" />
              <div className="text-primary">{t('addCrop')}</div>
            </button>
          )}

          <div className="border border-b my-8" />
          <ProductCrop
            productType={productType}
            productSelect={productSelect}
            nv={nv}
            setNv={setNv}
            mt={mt}
            setMt={setMt}
            setProductType={setProductType}
            getProductStatus={getProductStatus}
            errorNv={errorNv}
            errorMt={errorMt}
            surface={area}
            setSurface={setArea}
            getMtha={getMtha}
            getEuroHa={getEuroHa}
            getErrorOfSurface={getErrorOfArea}
            getProductMt={getProductMt}
            disabled={getDisabled && !isEditing}
          />

          <div className="border border-b my-8" />
          <CropFieldTotal
            getCost={getCost}
            getGains={getGains}
            getROI={getROI}
          />
          {!isPDF && (
            <>
              <div className="flex justify-center items-center flex-col">
                <div className="flex items-center mt-6 md:flex-col">
                  <div className="flex md:mb-6">
                    <button
                      className={`${
                        getDisabled && !isEditing
                          ? 'cursor-not-allowed'
                          : 'cursor-pointer'
                      } flex items-center text-primary mr-4 md:mr-2`}
                      onClick={() => resetData(id)()}
                      disabled={getDisabled && !isEditing}
                    >
                      <span className="mr-3">
                        <img src={resetIcon} alt="" />
                      </span>
                      {t('resetData')}
                    </button>
                    <button
                      className="cursor-pointer flex items-center text-primary ml-4 md:ml-2 md:mr-4"
                      onClick={onDuplicateField}
                    >
                      <span className="mr-3">
                        <img src={duplicateIcon} alt="" />
                      </span>
                      {t('duplicatedField')}
                    </button>
                  </div>

                  {fields.length > 1 && (
                    <button
                      className="cursor-pointer flex items-center text-red500 ml-4"
                      onClick={onDeleteClick}
                    >
                      <span className="mr-3">
                        <img src={deleteIcon} alt="" />
                      </span>
                      {t('deleteField')}
                    </button>
                  )}
                </div>
                <div className="mt-6">
                  {!getDisabled ? (
                    <>
                      <Button
                        disabled={name.length < 1 || getNameDisabled}
                        onClick={addToSupplyPlan}
                      >
                        {t('addToSupply')}
                      </Button>
                      <div className="text-[#FF3B30] text-xs ml-1 h-2 flex items-center justify-center mt-2">
                        {name.length < 1 && <div>{t('nameError')}</div>}
                      </div>
                    </>
                  ) : (
                    <>
                      {isEditing ? (
                        <Button
                          disabled={name.length < 1}
                          onClick={updateInSupplyPlan}
                        >
                          {t('updateSupply')}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => {
                            setIsEditing(true);
                            setInitialName(name);
                          }}
                          className="w-[235px]"
                          btnType="secondary"
                        >
                          {t('edit')}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {addCropModal && (
                <AddCropModal
                  setAddCropModal={setAddCropModal}
                  crop={cropList}
                  tableData={tableData}
                  setTableData={setTableData}
                  fertilizationValues={fertilizationValues}
                  object={object}
                  cropIndex={tableData.length}
                />
              )}
              {!!editCropModal && (
                <EditCropModal
                  setEditCropModal={setEditCropModal}
                  editCropModal={editCropModal}
                  crop={cropList}
                  setTableData={setTableData}
                  object={object}
                  cropIndex={tableData.findIndex(
                    (item) => item.id === editCropModal.id,
                  )}
                />
              )}
              {deleteModal && (
                <Modal
                  saveBtnText={t('delete')}
                  cancelBtnText={t('cancel')}
                  title={t('delete')}
                  text={t('deleteText')}
                  onCancelClick={() => setDeleteModal(false)}
                  onSaveClick={() => {
                    setDeleteModal(false);
                    onDelete();
                  }}
                />
              )}
              {deleteModalWithoutSupplyPlan && (
                <Modal
                  saveBtnText={t('delete')}
                  cancelBtnText={t('cancel')}
                  title={t('delete')}
                  text={t('deleteTextWithoutSupply')}
                  onCancelClick={() => setDeleteModalWithoutSupplyPlan(false)}
                  onSaveClick={() => {
                    setDeleteModalWithoutSupplyPlan(false);
                    onDelete();
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
